// Conversaciones por canal: barras horizontales con el valor al final.
//
// whatsapp_unofficial (Evolution) se muestra SEPARADO de whatsapp a propósito:
// aquí es información real de operación — a diferencia del catálogo de
// respuestas rápidas, donde ambos se colapsan porque comparten plantillas.

import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, Tooltip, LabelList, ResponsiveContainer } from "recharts";
import { Card } from "../../../../components/ui/Card";
import { useChartColors } from "./chartTheme";
import { formatInt } from "./format";

const fmtTooltipValue = (value, name) => [formatInt(value), name];

/**
 * Etiqueta local de un canal. Nombres propios sin t(); solo Portal y el
 * calificativo "No Oficial" son traducibles.
 * @param {string} channel
 * @param {Function} t
 * @returns {string}
 */
function channelLabel(channel, t) {
  switch (channel) {
    case "whatsapp":
      return "WhatsApp";
    case "whatsapp_unofficial":
      return t("WhatsApp No Oficial");
    case "messenger":
      return "Messenger";
    case "instagram":
      return "Instagram";
    case "portal":
      return t("Portal");
    default:
      // Canal desconocido: se muestra crudo antes que ocultar datos reales.
      return channel || "—";
  }
}

/**
 * @param {object} props
 * @param {Array<{channel: string, count: number}>} props.rows - por_canal del backend
 * @param {string} [props.className]
 */
function ChannelChart({ rows, className }) {
  const { t } = useTranslation();
  const { colors, axisTick, tooltipStyle, tooltipLabelStyle } = useChartColors();

  // Orden desc: la comparación de magnitudes se lee de arriba hacia abajo.
  const data = useMemo(
    () =>
      (rows || [])
        .map((r) => ({ label: channelLabel(r.channel, t), count: r.count || 0 }))
        .sort((a, b) => b.count - a.count),
    [rows, t],
  );

  const hasData = data.length > 0;
  // Altura proporcional a las filas para que 2 canales no floten en 280px de aire.
  const height = Math.max(120, data.length * 44 + 24);

  return (
    <Card padding="md" className={className}>
      <h2 className="text-sm font-semibold text-fg mb-3">{t("Conversaciones por canal")}</h2>

      {hasData ? (
        <div
          role="img"
          aria-label={t("Gráfico de barras con el total de conversaciones por canal.")}
        >
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 8 }}>
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="label"
                width={130}
                axisLine={false}
                tickLine={false}
                tick={axisTick}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={tooltipLabelStyle}
                formatter={fmtTooltipValue}
                cursor={{ fill: colors.bgMuted }}
              />
              <Bar
                dataKey="count"
                name={t("Conversaciones")}
                fill={colors.brand}
                maxBarSize={22}
                radius={[0, 3, 3, 0]}
                isAnimationActive={false}
              >
                {/* Valor al final de cada barra: evita que el lector tenga que
                    estimar contra un eje que está oculto. */}
                <LabelList
                  dataKey="count"
                  position="right"
                  formatter={formatInt}
                  style={{ fontSize: 11, fill: colors.fgMuted }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[120px] flex items-center justify-center text-[12px] md:text-[11px] text-fg-muted">
          {t("Sin conversaciones en el período.")}
        </div>
      )}
    </Card>
  );
}

export default memo(ChannelChart);
